<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, Save, Database, Users, Table2, FileText, RefreshCw, FileCode, Download, ChevronLeft, ChevronRight, Eye, EyeOff, Copy, Key } from 'lucide-vue-next'
import { adminApi } from '@/services/api'
import { toast } from '@/utils/toast'
import { useAdminAuth } from '@/composables/useAdminAuth'
import type { Tenant, Base, User, Table as DBTable, GenerationRun, Template } from '@/types'

const route = useRoute()
const router = useRouter()
const { apiKey, authenticated, ensureAuthenticated } = useAdminAuth()

// State
const tenant = ref<Tenant | null>(null)
const bases = ref<Base[]>([])
const users = ref<User[]>([])
const tables = ref<DBTable[]>([])
const generations = ref<GenerationRun[]>([])
const templates = ref<Template[]>([])
const saving = ref(false)
const activeTab = ref('details') // details, bases, users, tables, templates, generations

// Pagination state for generations
const generationsPage = ref(1)
const generationsLimit = ref(20)
const generationsPagination = ref({ page: 1, limit: 20, total: 0, totalPages: 0 })

// Webhook API key state
const showWebhookApiKey = ref(false)
const regenerating = ref(false)

// Edit form
const editForm = ref({
  name: '',
  plan: 'free' as 'free' | 'pro',
  monthly_quota: null as number | null,
  quota_reset_day_number: null as number | null,
  enforce_quota: false,
  max_templates: 2,
  template_retention_days: 30,
  generation_retention_days: 1,
})

// Load all tenant data
const loadTenantData = async () => {
  const key = ensureAuthenticated()
  if (!key) {
    router.push('/admin')
    return
  }

  const tenantKey = route.params.tenantKey as string

  try {
    // Load tenant details
    tenant.value = await adminApi.getTenant(key, tenantKey)

    // Populate edit form
    editForm.value = {
      name: tenant.value.name || '',
      plan: (tenant.value.plan || 'free') as 'free' | 'pro',
      monthly_quota: tenant.value.monthly_quota,
      quota_reset_day_number: tenant.value.quota_reset_day_number,
      enforce_quota: tenant.value.enforce_quota === 1,
      max_templates: tenant.value.max_templates || 2,
      template_retention_days: tenant.value.template_retention_days || 30,
      generation_retention_days: tenant.value.generation_retention_days || 1,
    }

    // Load related data in parallel
    const [basesData, usersData, tablesData, templatesData, generationsData] = await Promise.all([
      adminApi.getTenantBases(key, tenantKey),
      adminApi.getTenantUsers(key, tenantKey),
      adminApi.getTenantTables(key, tenantKey),
      adminApi.getTenantTemplates(key, tenantKey),
      adminApi.getTenantGenerations(key, tenantKey, generationsPage.value, generationsLimit.value),
    ])

    bases.value = basesData
    users.value = usersData
    tables.value = tablesData
    templates.value = templatesData
    generations.value = generationsData.generations
    generationsPagination.value = generationsData.pagination
  } catch (error: any) {
    console.error('Failed to load tenant data:', error)
    toast.error('Failed to load tenant data: ' + error.message)
    if (error.message.includes('403') || error.message.includes('401')) {
      authenticated.value = false
      router.push('/admin')
    }
  }
}

// Save tenant changes
const saveTenant = async () => {
  const key = apiKey.value
  if (!key || !tenant.value) return

  saving.value = true
  try {
    const updatedTenant = await adminApi.updateTenant(
      key,
      tenant.value.lark_tenant_key,
      editForm.value
    )
    tenant.value = updatedTenant
    toast.success('Tenant updated successfully')
  } catch (error: any) {
    console.error('Failed to update tenant:', error)
    toast.error('Failed to update tenant: ' + error.message)
  } finally {
    saving.value = false
  }
}

// Pagination functions
const loadGenerationsPage = async (page: number) => {
  const key = apiKey.value
  if (!key || !tenant.value) return

  try {
    const tenantKey = route.params.tenantKey as string
    generationsPage.value = page
    const data = await adminApi.getTenantGenerations(key, tenantKey, page, generationsLimit.value)
    generations.value = data.generations
    generationsPagination.value = data.pagination
  } catch (error: any) {
    console.error('Failed to load generations:', error)
    toast.error('Failed to load generations: ' + error.message)
  }
}

const nextGenerationsPage = () => {
  if (generationsPage.value < generationsPagination.value.totalPages) {
    loadGenerationsPage(generationsPage.value + 1)
  }
}

const prevGenerationsPage = () => {
  if (generationsPage.value > 1) {
    loadGenerationsPage(generationsPage.value - 1)
  }
}

// Download functions
const downloadTemplate = async (template: Template) => {
  const key = apiKey.value
  if (!key) return

  try {
    await adminApi.downloadTemplate(key, template.id, template.original_filename || `${template.name}.docx`)
    toast.success('Template downloaded successfully')
  } catch (error: any) {
    console.error('Failed to download template:', error)
    toast.error('Failed to download template: ' + error.message)
  }
}

const downloadGeneration = async (generation: GenerationRun) => {
  const key = apiKey.value
  if (!key) return

  try {
    await adminApi.downloadGeneration(key, generation.id, generation.output_filename || 'generated-document.docx')
    toast.success('Generation downloaded successfully')
  } catch (error: any) {
    console.error('Failed to download generation:', error)
    toast.error('Failed to download generation: ' + error.message)
  }
}

// Format timestamp
const formatDate = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A'
  return new Date(timestamp).toLocaleString()
}

// Format relative time
const formatRelativeTime = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A'
  const now = Date.now()
  const diff = now - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return `${days}d ago`
}

// Webhook API key functions
const copyWebhookApiKey = () => {
  if (!tenant.value?.webhook_api_key) return
  navigator.clipboard.writeText(tenant.value.webhook_api_key)
  toast.success('Webhook API key copied to clipboard')
}

const generateWebhookApiKey = async () => {
  const key = apiKey.value
  if (!key || !tenant.value) return

  regenerating.value = true
  try {
    const newApiKey = await adminApi.generateWebhookApiKey(key, tenant.value.lark_tenant_key)

    // Update tenant with new API key
    if (tenant.value) {
      tenant.value.webhook_api_key = newApiKey
    }

    toast.success('Webhook API key generated successfully')
  } catch (error: any) {
    console.error('Failed to generate webhook API key:', error)
    toast.error('Failed to generate webhook API key: ' + error.message)
  } finally {
    regenerating.value = false
  }
}

// On mount
onMounted(() => {
  loadTenantData()
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 pb-12">
    <!-- Header -->
    <div class="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
      <div class="max-w-7xl mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <button
              @click="router.push('/admin')"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft class="w-5 h-5" />
            </button>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">
                {{ tenant?.name || 'Loading...' }}
              </h1>
              <p class="text-sm text-gray-500 font-mono">{{ route.params.tenantKey }}</p>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              @click="loadTenantData"
              class="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw class="w-4 h-4" />
              Refresh
            </button>
            <button
              @click="saveTenant"
              :disabled="saving"
              class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              <Save class="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="max-w-7xl mx-auto px-6">
        <div class="flex gap-6 border-b border-gray-200">
          <button
            @click="activeTab = 'details'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors': true,
              'border-blue-500 text-blue-600': activeTab === 'details',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'details',
            }"
          >
            Details
          </button>
          <button
            @click="activeTab = 'bases'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors flex items-center gap-2': true,
              'border-blue-500 text-blue-600': activeTab === 'bases',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'bases',
            }"
          >
            <Database class="w-4 h-4" />
            Bases ({{ bases.length }})
          </button>
          <button
            @click="activeTab = 'users'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors flex items-center gap-2': true,
              'border-blue-500 text-blue-600': activeTab === 'users',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'users',
            }"
          >
            <Users class="w-4 h-4" />
            Users ({{ users.length }})
          </button>
          <button
            @click="activeTab = 'tables'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors flex items-center gap-2': true,
              'border-blue-500 text-blue-600': activeTab === 'tables',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'tables',
            }"
          >
            <Table2 class="w-4 h-4" />
            Tables ({{ tables.length }})
          </button>
          <button
            @click="activeTab = 'templates'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors flex items-center gap-2': true,
              'border-blue-500 text-blue-600': activeTab === 'templates',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'templates',
            }"
          >
            <FileCode class="w-4 h-4" />
            Templates ({{ templates.length }})
          </button>
          <button
            @click="activeTab = 'generations'"
            :class="{
              'pb-3 border-b-2 font-medium transition-colors flex items-center gap-2': true,
              'border-blue-500 text-blue-600': activeTab === 'generations',
              'border-transparent text-gray-600 hover:text-gray-900': activeTab !== 'generations',
            }"
          >
            <FileText class="w-4 h-4" />
            Generations ({{ generations.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-7xl mx-auto px-6 py-6">
      <!-- Details Tab -->
      <div v-if="activeTab === 'details'" class="space-y-6">
        <div class="bg-white rounded-lg shadow p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Tenant Information</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                v-model="editForm.name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tenant name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Plan</label>
              <select
                v-model="editForm.plan"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Monthly Quota
                <span class="text-xs text-gray-500 font-normal ml-1">(leave empty for unlimited)</span>
              </label>
              <input
                v-model.number="editForm.monthly_quota"
                type="number"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Current Usage</label>
              <div class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                <span v-if="tenant?.monthly_quota === null" class="text-gray-700">
                  {{ tenant?.current_quota || 0 }}
                  <span class="text-xs text-green-600 italic ml-2">
                    (Unlimited)
                  </span>
                </span>
                <span v-else-if="tenant?.current_quota !== null"
                  :class="{
                    'font-medium': true,
                    'text-yellow-600': (tenant?.current_quota || 0) >= (tenant?.monthly_quota || 0) * 0.8,
                    'text-red-600': (tenant?.current_quota || 0) >= (tenant?.monthly_quota || 0),
                    'text-gray-700': (tenant?.current_quota || 0) < (tenant?.monthly_quota || 0) * 0.8
                  }"
                >
                  {{ tenant?.current_quota }} / {{ tenant?.monthly_quota }}
                  <span v-if="(tenant?.current_quota || 0) >= (tenant?.monthly_quota || 1) * 0.8" class="text-xs ml-1">
                    ({{ Math.round(((tenant?.current_quota || 0) / (tenant?.monthly_quota || 1)) * 100) }}%)
                  </span>
                </span>
                <span v-else class="text-gray-500">
                  -
                </span>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Quota Reset Day (1-31)
                <span class="text-xs text-gray-500 font-normal ml-1">(leave empty if no quota)</span>
              </label>
              <input
                v-model.number="editForm.quota_reset_day_number"
                type="number"
                min="1"
                max="31"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="N/A"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Enforce Quota</label>
              <div class="flex items-center gap-3">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="editForm.enforce_quota"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span class="text-sm text-gray-600">
                  {{ editForm.enforce_quota ? 'Hard limit (blocks generations)' : 'Tracking only' }}
                </span>
              </div>
              <p class="text-xs text-gray-500 mt-1">When enabled, generations will be blocked if quota is exceeded</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Max Templates</label>
              <input
                v-model.number="editForm.max_templates"
                type="number"
                min="1"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="2"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Template Retention Days</label>
              <input
                v-model.number="editForm.template_retention_days"
                type="number"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="30"
              />
              <p class="text-xs text-gray-500 mt-1">Days of inactivity before template deletion (0 = never delete)</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Generation Retention Days</label>
              <input
                v-model.number="editForm.generation_retention_days"
                type="number"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1"
              />
              <p class="text-xs text-gray-500 mt-1">Days before generation deletion (0 = never delete)</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Created At</label>
              <input
                :value="formatDate(tenant?.created_at)"
                type="text"
                disabled
                class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>
          </div>

          <!-- Webhook API Key -->
          <div class="mt-6 pt-6 border-t border-gray-200">
            <div class="flex items-center gap-2 mb-4">
              <Key class="w-4 h-4 text-gray-700" />
              <h3 class="text-sm font-semibold text-gray-900">Webhook API Key</h3>
            </div>

            <div v-if="tenant?.webhook_api_key" class="space-y-3">
              <div class="flex gap-2">
                <div class="relative flex-1">
                  <input
                    :type="showWebhookApiKey ? 'text' : 'password'"
                    :value="tenant.webhook_api_key"
                    readonly
                    class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg bg-gray-50 text-xs font-mono"
                  />
                  <button
                    @click="showWebhookApiKey = !showWebhookApiKey"
                    class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                    type="button"
                  >
                    <Eye v-if="!showWebhookApiKey" class="w-4 h-4" />
                    <EyeOff v-else class="w-4 h-4" />
                  </button>
                </div>
                <button
                  @click="copyWebhookApiKey"
                  class="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                  type="button"
                >
                  <Copy class="w-4 h-4" />
                </button>
                <button
                  @click="generateWebhookApiKey"
                  :disabled="regenerating"
                  class="px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                  type="button"
                  title="Regenerate (will invalidate old key)"
                >
                  <RefreshCw :class="{ 'w-4 h-4': true, 'animate-spin': regenerating }" />
                </button>
              </div>
            </div>

            <div v-else>
              <button
                @click="generateWebhookApiKey"
                :disabled="regenerating"
                class="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2 text-sm"
                type="button"
              >
                <Key class="w-4 h-4" />
                Generate API Key
              </button>
              <p class="text-xs text-gray-500 mt-2">No API key found. New tenants should have one auto-generated.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bases Tab -->
      <div v-else-if="activeTab === 'bases'">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Token</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personal Token</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="bases.length === 0">
                  <td colspan="3" class="px-6 py-12 text-center text-gray-500">No bases found</td>
                </tr>
                <tr v-for="base in bases" :key="base.lark_base_token">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ base.lark_base_token }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{{ base.personal_base_token || '-' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(base.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Users Tab -->
      <div v-else-if="activeTab === 'users'">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="users.length === 0">
                  <td colspan="2" class="px-6 py-12 text-center text-gray-500">No users found</td>
                </tr>
                <tr v-for="user in users" :key="user.lark_user_id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ user.lark_user_id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(user.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Tables Tab -->
      <div v-else-if="activeTab === 'tables'">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Table ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Token</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="tables.length === 0">
                  <td colspan="3" class="px-6 py-12 text-center text-gray-500">No tables found</td>
                </tr>
                <tr v-for="table in tables" :key="table.lark_table_id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ table.lark_table_id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{{ table.lark_base_token }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(table.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Templates Tab -->
      <div v-else-if="activeTab === 'templates'">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Table ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">R2 Key</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="templates.length === 0">
                  <td colspan="6" class="px-6 py-12 text-center text-gray-500">No templates found</td>
                </tr>
                <tr v-for="template in templates" :key="template.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ template.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ template.name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{{ template.lark_table_id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">{{ template.r2_key || '-' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(template.created_at) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      v-if="template.r2_key"
                      @click="downloadTemplate(template)"
                      class="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      title="Download template"
                    >
                      <Download class="w-4 h-4" />
                    </button>
                    <span v-else class="text-gray-400">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Generations Tab -->
      <div v-else-if="activeTab === 'generations'" class="space-y-4">
        <div class="bg-white rounded-lg shadow overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Output File</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MIME Type</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quota Cost</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="generations.length === 0">
                  <td colspan="8" class="px-6 py-12 text-center text-gray-500">No generations found</td>
                </tr>
                <tr v-for="gen in generations" :key="gen.id" :class="gen.deleted_at ? 'opacity-50' : ''">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ gen.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{{ gen.template_id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      v-if="gen.deleted_at"
                      class="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600"
                    >
                      expired
                    </span>
                    <span
                      v-else
                      :class="{
                        'px-2 py-1 text-xs rounded-full': true,
                        'bg-green-100 text-green-800': gen.status === 'completed',
                        'bg-red-100 text-red-800': gen.status === 'failed',
                      }"
                    >
                      {{ gen.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{{ gen.output_filename || '-' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ gen.file_mimetype || '-' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">{{ gen.quota_cost }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500" :title="formatDate(gen.created_at)">
                    {{ formatRelativeTime(gen.created_at) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      v-if="gen.status === 'completed' && gen.r2_key && !gen.deleted_at"
                      @click="downloadGeneration(gen)"
                      class="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      title="Download generation"
                    >
                      <Download class="w-4 h-4" />
                    </button>
                    <span v-else-if="gen.deleted_at" class="text-gray-400 italic">expired</span>
                    <span v-else class="text-gray-400">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div v-if="generationsPagination.totalPages > 1" class="bg-white rounded-lg shadow px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing page {{ generationsPagination.page }} of {{ generationsPagination.totalPages }}
              ({{ generationsPagination.total }} total generations)
            </div>
            <div class="flex gap-2">
              <button
                @click="prevGenerationsPage"
                :disabled="generationsPagination.page === 1"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft class="w-4 h-4" />
                Previous
              </button>
              <button
                @click="nextGenerationsPage"
                :disabled="generationsPagination.page === generationsPagination.totalPages"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Component-specific styles if needed */
</style>
